import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import Panel from './Panel.jsx';
import { listHolders, deactivateHolder } from '@/services/coi.service.js';
import { toast } from '@/components/ui/toaster.jsx';

export default function CertHoldersPanel({ accountId }) {
  const [holders, setHolders] = useState([]);
  const [loading, setLoading] = useState(false);

  function refresh() {
    if (!accountId) return;
    setLoading(true);
    listHolders(accountId)
      .then((res) => setHolders(res?.holders || []))
      .catch(() => setHolders([]))
      .finally(() => setLoading(false));
  }

  useEffect(() => { refresh(); }, [accountId]);

  async function handleDeactivate(h) {
    try {
      await deactivateHolder(h.id);
      setHolders((prev) => prev.filter((x) => x.id !== h.id));
      toast.success(`"${h.name}" removed`);
    } catch (e) {
      toast.error(e.message || 'Failed to remove holder');
    }
  }

  if (loading) {
    return (
      <Panel title="Certificate Holders">
        <p className="text-xs text-text-dim">Loading…</p>
      </Panel>
    );
  }

  return (
    <Panel
      title="Certificate Holders"
      count={holders.length}
      empty={holders.length === 0 ? 'No certificate holders on file. Add one when a holder requests proof of insurance.' : undefined}
    >
      {holders.length > 0 ? (
        <div className="space-y-1.5">
          {holders.map((h) => (
            <HolderRow key={h.id} holder={h} onDeactivate={handleDeactivate} />
          ))}
        </div>
      ) : null}
    </Panel>
  );
}

function HolderRow({ holder, onDeactivate }) {
  return (
    <div className="flex items-center justify-between rounded-card border border-border px-3 py-2">
      <div className="min-w-0">
        <div className="text-sm font-medium text-text truncate">{holder.name}</div>
        <div className="text-[11px] text-text-muted">{holder.city}, {holder.state} {holder.zip}</div>
      </div>
      <div className="flex items-center gap-2">
        {holder.email ? (
          <div className="text-[10px] text-text-dim">{holder.email}</div>
        ) : null}
        <button
          type="button"
          onClick={() => onDeactivate(holder)}
          className="rounded p-1 text-text-dim hover:bg-red-500/10 hover:text-red-400 transition-colors"
          title="Remove holder"
        >
          <X size={14} strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
}
